import { InputNumber } from 'primeng/inputnumber';

/**
 * Marca de idempotencia: el guard se instala en el prototipo, así que hay que poder distinguir un
 * prototipo ya parcheado de uno de fábrica.
 */
const GUARD_FLAG = '__erpInputNumberKeyGuard';

type GuardedPrototype = typeof InputNumber.prototype & {
  [GUARD_FLAG]?: true;
  isMinusSign(char: string): boolean;
  isDecimalSign(char: string): boolean;
  _decimalChar: string;
};

/**
 * Repone el guard de teclado que `InputNumber` se salta cuando hay texto seleccionado (issue #417).
 *
 * `onInputKeyPress` filtra la tecla contra `(48 <= code && code <= 57) || isMinusSign || isDecimalSign`
 * —dígitos, signo menos y separador decimal— **pero sólo si el campo no tiene selección**. Con texto
 * seleccionado sale antes por su propia rama:
 *
 * ```js
 * if (selectionStart !== selectionEnd && selectedValueStr.length > 0) {
 *   this.insert(event, char, { isDecimalSign, isMinusSign });
 *   return;   // ← se salta el filtro de más abajo
 * }
 * ```
 *
 * Así llega cualquier carácter a `insert()`, y de ahí a `updateInput()`, que en la rama
 * `range-insert` construye la expresión con el texto insertado **sin escaparlo**:
 * `new RegExp(insertedValueStr.split('').join(...), 'g')`. Con un `+` eso es `/+/g` y lanza
 * `SyntaxError: Nothing to repeat`, que es el crash reportado (Rollbar item 47). Le pasa igual a
 * `* ? ( ) [ ] { } | ^ $` y a la barra invertida.
 *
 * **No es un problema de configuración nuestra:** en PrimeNG 21.1.9 `prefix`, `suffix` y `currency`
 * SÍ se escapan (`escapeRegExp`), así que el símbolo del tenant no es el vector. Se reproduce en
 * cualquiera de los ~146 `p-inputNumber` del repo: seleccionar el contenido y pulsar `+`.
 *
 * **Por qué se parchea el prototipo y no se usa una directiva:** una directiva de Angular hay que
 * importarla en cada componente que la use, y aquí serían 146 —más el 147, que se olvidaría—. El
 * parche aplica el filtro que PrimeNG ya declara como suyo, sobre `this`, de modo que
 * `isMinusSign`/`isDecimalSign` siguen siendo los de la instancia (locale y modo incluidos) en vez
 * de una copia que se desincronice. Se instala una sola vez, desde `app.config.ts`.
 *
 * Es reversible por naturaleza: cuando PrimeNG añada el filtro, este guard pasa a ser un no-op
 * porque descarta exactamente las mismas teclas.
 *
 * **Segunda responsabilidad: el «.» de la fila principal es decimal (issue #889).** Los montos de
 * finanzas fijan `locale="es-VE"` para que la coma sea el decimal con independencia del navegador.
 * En ese locale el «.» es el separador de miles, y `InputNumber` descarta esa tecla; quien teclea
 * `1500.75` desde un navegador `es-419` o `en` (donde el «.» es el decimal) obtenía `150075`, un
 * monto 100 veces mayor y **sin ningún aviso**. Se trata como lo que el usuario quiere decir: si el
 * «.» no es el decimal de la instancia, se normaliza a `NumpadDecimal`, que PrimeNG ya convierte en
 * el decimal del locale (`_decimalChar`). Donde el «.» ya es el decimal (en-US, es-419) no cambia nada.
 *
 * Tradeoff aceptado: teclear a mano el «.» de miles ya no se ignora, se lee como decimal. Es el mal
 * menor: nadie escribe los miles a mano (el campo los agrupa solo) y el error contrario era silencioso.
 *
 * **Tercera responsabilidad: el pegado de «1500.75» (issue #890, comentario del 2026-10-08, punto 3).**
 * Lo mismo ocurre al pegar: en es-VE `parseValue` descarta el «.» como separador de miles y
 * `1500.75` pasaba a 150075. Antes de delegar en `onPaste` se reescribe el «.» por el decimal de la
 * instancia, pero sólo cuando es inequívoco: un único «.», ninguna «,» y 1–2 dígitos detrás
 * (`1500.75` → `1500,75`, `12.5` → `12,5`). Con 3 dígitos (`1.500`) es agrupación de miles y no se
 * toca, igual que `1.500,75` o `1.500.75`. Se ignoran los espacios, el signo y el prefijo de moneda
 * (`$ 1500.75`, `Bs. 12.5`). Donde el «.» ya es el decimal (en-US, es-419) no cambia nada.
 */
export function applyInputNumberKeyGuard(): void {
  const proto = InputNumber.prototype as GuardedPrototype;
  if (proto[GUARD_FLAG]) return;

  const original = proto.onInputKeyPress;

  proto.onInputKeyPress = function (this: GuardedPrototype, event: KeyboardEvent): void {
    if (isMainRowPeriodNotDecimal(this, event)) asNumpadDecimal(event);
    if (!isAcceptedKey(this, event)) {
      event.preventDefault();
      return;
    }
    original.call(this, event);
  };

  const originalPaste = proto.onPaste;

  proto.onPaste = function (this: GuardedPrototype, event: ClipboardEvent): void {
    normalizePastedDecimal(this, event);
    originalPaste.call(this, event);
  };

  proto[GUARD_FLAG] = true;
}

/**
 * Texto pegado con un único «.» decimal inequívoco: prefijo sin dígitos ni «,» (signo, moneda),
 * parte entera, «.» y 1–2 dígitos. Nada de miles (3 dígitos), nada de «,». Excepción: con parte
 * entera 0 el «.» es decimal sin ambigüedad (`0.125` no puede ser ciento veinticinco).
 */
const PASTED_PERIOD_DECIMAL = /^([^\d,]*?)(\d+)\.(\d{1,2})$|^([^\d,]*?)(0)\.(\d+)$/;

/**
 * Reescribe el «.» del texto pegado por el decimal de la instancia cuando éste no es el «.».
 *
 * `clipboardData` es un getter del prototipo de `ClipboardEvent`: se sombrea con una propiedad
 * propia vía `defineProperty` (mismo motivo que `asNumpadDecimal`), de modo que el `onPaste`
 * original lee el texto normalizado y cancela el mismo evento que está en vuelo.
 */
function normalizePastedDecimal(instance: GuardedPrototype, event: ClipboardEvent): void {
  if (instance.isDecimalSign('.')) return;
  const text = event.clipboardData?.getData('Text');
  const match = text ? PASTED_PERIOD_DECIMAL.exec(text.trim()) : null;
  if (!match) return;

  const [prefix, integer, fraction] = match[2] !== undefined ? match.slice(1, 4) : match.slice(4, 7);
  const normalized = `${prefix}${integer}${instance._decimalChar}${fraction}`;
  Object.defineProperty(event, 'clipboardData', {
    value: { getData: () => normalized },
    configurable: true,
  });
}

/** El «.» de la fila principal (keyCode 46) en una instancia cuyo decimal no es el «.». */
function isMainRowPeriodNotDecimal(instance: GuardedPrototype, event: KeyboardEvent): boolean {
  const code = event.which || event.keyCode;
  return code === 46 && event.code !== 'NumpadDecimal' && !instance.isDecimalSign('.');
}

/**
 * Hace que PrimeNG lea el evento como la tecla decimal del teclado numérico (`event.code`).
 *
 * `code` es un getter del prototipo de `KeyboardEvent`, no una propiedad propia: se sombrea con una
 * propiedad propia vía `defineProperty`, que funciona igual en jsdom y en Chromium. Asignar
 * `event.code = ...` fallaría en silencio (getter sin setter) y recrear el evento no sirve, porque
 * el original ya está en vuelo y es el que PrimeNG cancela con `preventDefault()`.
 */
function asNumpadDecimal(event: KeyboardEvent): void {
  Object.defineProperty(event, 'code', { value: 'NumpadDecimal', configurable: true });
}

/**
 * El mismo criterio que `InputNumber` aplica cuando NO hay selección: dígito, signo menos o
 * separador decimal. Sólo se exime Enter (13), que no aporta carácter.
 *
 * **No se exime un evento por llevar modificador.** `AltGr` no es un atajo: produce carácter, y en
 * Windows se reporta como `ctrlKey + altKey`. En los teclados latinoamericano y español es
 * precisamente la tecla con la que se escriben `[ ] { } \ |` —todos metacaracteres de expresión
 * regular—, así que una exención por modificador dejaba el crash de #417 intacto justo en el
 * teclado que usa este ERP. Los atajos reales (Ctrl+A, Ctrl+C, Ctrl+V) no llegan aquí: `keypress`
 * sólo dispara para caracteres imprimibles y Enter, y el pegado va por `onPaste`.
 */
function isAcceptedKey(instance: GuardedPrototype, event: KeyboardEvent): boolean {
  const code = event.which || event.keyCode;
  if (code === 13) return true;
  if (code >= 48 && code <= 57) return true;

  // La tecla decimal del teclado numérico la normaliza el propio componente.
  if (event.code === 'NumpadDecimal') return true;

  const char = String.fromCharCode(code);
  return instance.isMinusSign(char) || instance.isDecimalSign(char);
}
